/**
 * Canonical date formatting utilities.
 *
 * ALL dates render US format — MM/DD/YYYY — on every surface, including
 * downloaded PDFs. Ruled 2026-08 by the operator. Long-form variants are
 * an explicit per-surface decision that lives IN THIS FILE, never at the
 * call site.
 *
 * Registered as the single source of truth for date display. **Any second
 * implementation of date formatting is a defect, not a shortcut.** This
 * mirrors the convention already established for currency (see
 * formatCurrency.js) — money has had one formatter with an explicit
 * prohibition on a second since Iter 12; dates had 56 bare
 * toLocaleDateString call sites and no formatter at all until IDI-101.
 *
 * IMPORTANT — null-handling contract:
 *   null | undefined | ""                   →  ""
 *   an unparseable string                   →  ""
 *   a Date object or ISO string             →  formatted output
 *
 * Callers relied on toLocaleDateString silently producing "Invalid Date"
 * as a literal string in the DOM when passed an absent value — the exact
 * class-of-defect that produced 1970-01-01 customer dates and other
 * "failure rendered as legitimate content" bugs elsewhere. Return "" here
 * so a genuinely absent date renders as an empty cell, not as
 * "Invalid Date".
 *
 * ISO INPUT IS SUPPORTED. ISO OUTPUT IS NOT.
 *   toISOString().split('T')[0] is CORRECT for API payloads and storage,
 *   and must never reach a rendered surface. If you have an ISO string
 *   heading to the DOM, run it through formatDate here — do not "fix"
 *   the stored value.
 *
 * @module utils/formatDate
 */

/**
 * Coerce a value to a Date, returning null when unparseable.
 * Internal helper; exposed only as `parseDate` below.
 *
 * @param {Date|string|number|null|undefined} value
 * @returns {Date|null}
 */
function toDate(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }
  // Special-case bare YYYY-MM-DD (no time part). The JS Date constructor
  // parses these as UTC midnight, so a caller in a US timezone sees the
  // previous calendar day when the string had no time component. That's a
  // silent off-by-one for `toISOString().split('T')[0]` values — one of
  // the exact upstream shapes IDI-101 was filed against. Parse as local
  // midnight instead so the day matches what the string says.
  if (typeof value === 'string') {
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, y, m, d] = dateOnlyMatch;
      const parsed = new Date(Number(y), Number(m) - 1, Number(d));
      return Number.isFinite(parsed.getTime()) ? parsed : null;
    }
  }
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
}

/**
 * Parse a date value the way the formatters do: a bare YYYY-MM-DD is LOCAL
 * midnight (not UTC), anything else goes through the Date constructor.
 * Exported for non-display arithmetic (e.g. invoice aging, IDI-337) so a
 * caller never re-implements the bare-date rule with `new Date(bare)`.
 *
 * @param {Date|string|number|null|undefined} value
 * @returns {Date|null} null for absent/unparseable input
 */
export function parseDate(value) {
  return toDate(value);
}

/**
 * Format a date as US MM/DD/YYYY.
 * Example: new Date('2026-08-31') → "08/31/2026"
 *
 * @param {Date|string|number|null|undefined} value
 * @returns {string} "MM/DD/YYYY" or "" for absent/unparseable input
 */
export function formatDate(value) {
  const d = toDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/**
 * Format a date + time as US MM/DD/YYYY H:MM AM/PM.
 * Example: "2026-08-31T14:14:00Z" → "08/31/2026 2:14 PM"  (in local tz)
 *
 * @param {Date|string|number|null|undefined} value
 * @returns {string} "MM/DD/YYYY H:MM AM/PM" or "" for absent/unparseable input
 */
export function formatDateTime(value) {
  const d = toDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

/**
 * Format the time of day as US H:MM AM/PM.
 * Example: "2026-08-31T14:14:00Z" → "2:14 PM"  (in local tz)
 *
 * @param {Date|string|number|null|undefined} value
 * @returns {string} "H:MM AM/PM" or "" for absent/unparseable input
 */
export function formatTime(value) {
  const d = toDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

// Notably absent: `formatDateLong` (e.g. "August 31, 2026").
//
// The stakeholder rule is US MM/DD/YYYY on every surface. Adding a
// long-form export before a surface genuinely needs one invites 56 call
// sites to migrate to formatDate() and then discover they wanted long-form
// after all. The right pattern when a specific surface needs long-form:
// add formatDateLong() HERE with its own docblock justifying which surface
// asked for it — not at the call site.
